import { Injectable } from '@angular/core';
import * as JSZip from 'jszip'; // Import JSZip for zip file creation
import { logDB } from './log-db'; // ⬅️ new import


export const LogModule = {
  App: 'APP',
  SIP: 'SIP',
  AUTH: 'AUTH',
  HTTP: 'HTTP',
  GRAPHQL: 'GRAPHQL',
  UI: 'UI',
};

export enum LogLevel {
  Info = 'INFO',
  Warn = 'WARN',
  Error = 'ERROR',
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
}


const aesKeyRaw = new TextEncoder().encode('9431GnSE7vZctwc331vhrc9zGW6VJSLv');
const iv = new Uint8Array(16);

async function importKey(): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', aesKeyRaw, 'AES-CBC', false, [
    'encrypt',
    'decrypt',
  ]);
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return buffer;
}

export async function encryptLogToBase64(plaintext: string): Promise<string> {
  const key = await importKey();
  const data = new TextEncoder().encode(plaintext);
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    key,
    data
  );
  return arrayBufferToBase64(encrypted);
}

export async function decryptLogFromBase64(base64: string): Promise<string> {
  const key = await importKey();
  const encrypted = base64ToArrayBuffer(base64);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv },
    key,
    encrypted
  );
  return new TextDecoder().decode(decrypted);
}

export async function encryptLogToBuffer(
  plaintext: string
): Promise<ArrayBuffer> {
  const key = await importKey();
  const data = new TextEncoder().encode(plaintext);
  return await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, data);
}

export function downloadEncryptedLog(buffer: ArrayBuffer): void {
  const blob = new Blob([buffer], { type: 'application/octet-stream' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'log.bin';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

@Injectable({
  providedIn: 'root',
})
export class LogService {
  constructor() {
    setInterval(() => {
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      logDB.logs
        .filter((log) => new Date(log.timestamp).getTime() < cutoff)
        .delete()
        .then((count) =>
          console.log(`🧹 Daily cleanup: removed ${count} logs`)
        );
    }, 24 * 60 * 60 * 1000);
  }

  public async logInfo(
    message: string,
    module: string = LogModule.App
  ): Promise<void> {
    await this.addLog(LogLevel.Info, message, module);
  }

  public async logWarn(
    message: string,
    module: string = LogModule.App
  ): Promise<void> {
    await this.addLog(LogLevel.Warn, message, module);
  }

  public async logError(
    message: string,
    module: string = LogModule.App
  ): Promise<void> {
    await this.addLog(LogLevel.Error, message, module);
  }

  private async addLog(
    level: LogLevel,
    message: string,
    module: string
  ): Promise<void> {
    // return new Promise<void>(async (resolve) => {resolve();});
    const logEntry: LogEntry = {
      level,
      message: `[${module}] ${message}`,
      timestamp: new Date(),
    };

    const encrypted = await encryptLogToBase64(JSON.stringify(logEntry));
    await logDB.logs.add({
      timestamp: logEntry.timestamp.toISOString(),
      encrypted: encrypted,
    });
  
    console.log(
      `[${logEntry.timestamp.toISOString()}] [${logEntry.level}] ${
        logEntry.message
      }`
    );
  }

  public async getDecryptedLogs(): Promise<LogEntry[]> {
    const results: LogEntry[] = [];
    const allLogs = await logDB.logs.toArray();
    for (const { encrypted } of allLogs) {
      try {
        const decrypted = await decryptLogFromBase64(encrypted);
        results.push(JSON.parse(decrypted));
      } catch (e) {
        console.warn('❌ Failed to decrypt log entry:', e);
      }
    }
    return results;
  }

  public async clearLogs() {
    await logDB.logs.clear();
  }

  public async uploadLogs(): Promise<void> {
    const logs = await this.getDecryptedLogs();
    console.log('📤 Uploading logs...', logs);
    // Add HTTP POST logic here later
  }

  public async downloadAllLogsAsZip(): Promise<void> {
    const zip = new JSZip();

    // Step 1: Filter logs within last 7 days
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const validLogs = await logDB.logs
      .filter((log) => new Date(log.timestamp).getTime() >= cutoff)
      .toArray();

    // Step 1.1: Delete expired logs (outside retention)
    const oldCount = await logDB.logs
      .filter((log) => new Date(log.timestamp).getTime() < cutoff)
      .delete();

    console.log(`🧹 Deleted ${oldCount} expired log(s)`);

    const groupedLogs: Record<string, string[]> = {};

    for (const { encrypted } of validLogs) {
      try {
        const decrypted = await decryptLogFromBase64(encrypted);
        const parsed: LogEntry = JSON.parse(decrypted);
        const dateKey = new Date(parsed.timestamp)
          .toISOString()
          .split('T')[0]
          .replace(/-/g, '');

        const formattedLine = `${parsed.timestamp} ${parsed.level} \n${parsed.message}`;
        if (!groupedLogs[dateKey]) groupedLogs[dateKey] = [];
        groupedLogs[dateKey].push(formattedLine);
        // groupedLogs[dateKey].push(decrypted);
      } catch (err) {
        console.warn('❌ Skipping invalid log during grouping:', err);
      }
    }

    console.log('Grouped logs by date:', groupedLogs);

    // Step 2: Encrypt grouped content and add to zip
    for (const dateKey in groupedLogs) {
      const lines = groupedLogs[dateKey].join('\r\n');
      const encryptedBuffer = await encryptLogToBuffer(lines); // ⬅️ returns ArrayBuffer

      zip.file(`log-${dateKey}.txt`, new Uint8Array(encryptedBuffer)); // still binary .txt
    }

    // Step 3: Trigger zip download
    const blob = await zip.generateAsync({ type: 'blob' });
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:]/g, '_');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `DiagnosticsLog_${timestamp}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
